import { useMemo } from "react";

import { AcceptSharedGoalModal } from "@components/Goals/AcceptSharedGoalModal";
import { BankRelinkModal, BankRelinkModalVariant } from "@components/Modals/BankRelinkModal";
import {
  CapitalGroupMigrationConsentModal,
  CapitalGroupMigrationOverrideModal,
} from "@components/Modals/CapitalGroupMigrationModal";
import { ShareGoalInviteStatus } from "@ctypes";
import { useReceivedSharedGoalInvites } from "@hooks/goals";
import { usePromotionalDollarQuery } from "@hooks/promo";
import { useFailedAchContribution, useNextTransfer } from "@hooks/transfers";
import { useUser } from "@hooks/user";
import { useModal } from "./useModal";
import { ModalConfig, useModalQueue } from "./useModalQueue";

const ModalIds = {
  CapitalGroupMigrationConsentModal: "CapitalGroupMigrationConsentModal",
  CapitalGroupMigrationOverrideModal: "CapitalGroupMigrationOverrideModal",
  BankRelinkModal: "BankRelinkModal",
  SharedGoalModal: "SharedGoalModal",
};

export const useDashboardModals = () => {
  const { settings, loading: userLoading, userId } = useUser();
  const { loading: nextTransferLoading } = useNextTransfer();
  const { loading: promoLoading } = usePromotionalDollarQuery();
  const { show } = useModal();

  const { data: sharedGoalInvites, loading: sharedGoalInvitesLoading } =
    useReceivedSharedGoalInvites();

  const hasPendingInvite = Boolean(
    sharedGoalInvites?.some((invite) => invite?.status === ShareGoalInviteStatus.Pending),
  );

  const bankRelinkVariant = (() => {
    if (settings?.achContributionFailedBankReLinkPopUp) {
      return BankRelinkModalVariant.ContributionFailed;
    }
    if (settings?.achContributionNotProcessedBankReLinkPopUp) {
      return BankRelinkModalVariant.RelinkFundingSource;
    }
    if (settings?.achConnectYourBankReLinkPopUp) {
      return BankRelinkModalVariant.ResumeContributions;
    }
    if (settings?.achUnauthorizedPopUp) {
      return BankRelinkModalVariant.AccountPaused;
    }
    return null;
  })();

  const { loading: failedAchLoading } = useFailedAchContribution({
    skip: !bankRelinkVariant,
  });

  const loading =
    userLoading ||
    nextTransferLoading ||
    promoLoading ||
    sharedGoalInvitesLoading ||
    failedAchLoading;

  const modalConfigs: ModalConfig[] = useMemo(() => {
    if (loading) return [];

    const scoped = (id: string) => (userId != null && userId !== "" ? `${id}:${userId}` : id);

    return [
      // The 529 migration runs to a 30-day deadline and approval only happens in-app, so it takes
      // the slot ahead of everything else, matching mobile. Approving flips
      // capitalGroupMigrationEnabled off, which stops it for good.
      {
        id: scoped(ModalIds.CapitalGroupMigrationConsentModal),
        shouldShow: Boolean(settings?.capitalGroupMigrationEnabled),
        action: () => show(<CapitalGroupMigrationConsentModal />),
      },
      {
        id: scoped(ModalIds.CapitalGroupMigrationOverrideModal),
        shouldShow: Boolean(settings?.capitalGroupMigrationOverride),
        action: () => show(<CapitalGroupMigrationOverrideModal />),
      },
      {
        id: scoped(ModalIds.BankRelinkModal),
        shouldShow: Boolean(bankRelinkVariant),
        action: () =>
          show(<BankRelinkModal variant={bankRelinkVariant as BankRelinkModalVariant} />),
      },
      {
        id: scoped(ModalIds.SharedGoalModal),
        shouldShow: hasPendingInvite,
        action: () => show(<AcceptSharedGoalModal />),
      },
    ];
  }, [loading, settings, bankRelinkVariant, hasPendingInvite, show, userId]);

  useModalQueue(modalConfigs);
};
